import { C } from "../../shared/theme";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ListRow } from "../../shared/ListRow";
import { Sortable, DragHandle, useDragSensors } from "../../shared/Sortable";
import { isReduceHabit, frequencyLabel, rowIds } from "./compassLists";

// ↑ for a habit you're building, ↓ for one you're cutting back: the Habits page and Today's Reminders both draw it.
// The glyph carries the meaning, in a quiet colour (green would read as a link, red as an error).
// `label` lets a screen reader hear it ("Cutting back") where nothing else around it says so.
export function HabitMark({ habit, label = false, style }) {
  const reduce = isReduceHabit(habit);
  return (
    <span role={label ? "img" : undefined} aria-label={label ? (reduce ? "Cutting back" : "Building") : undefined} aria-hidden={label ? undefined : "true"}
      style={{ fontWeight: 700, color: C.textMid, fontStyle: "normal", ...style }}>{reduce ? "↓" : "↑"}</span>
  );
}

// A habit, mindset, gratitude or temptation as a ListRow. `habit` is the stored habit on the Habits page, which starts
// the row with ↑ (building) or ↓ (cutting back), the same marks Today's Reminders shows. A habit's frequency is a pill
// under its words (Craig, 24 Oct 2026), here and on the Goal page, never on Today's Reminders. The rest is ListRow's.
export function CompassRow({ habit = null, ...row }) {
  if (habit == null) return <ListRow {...row} />;
  const reduce = isReduceHabit(habit);
  const often = frequencyLabel(habit);
  return (
    <ListRow {...row} marks={`${reduce ? "cutting back" : "building"}${often ? `, ${often.toLowerCase()}` : ""}`} pill={often}
      lead={<HabitMark habit={habit} style={{ flexShrink: 0, width: 14, fontSize: 16, lineHeight: 1.45 }} />} />
  );
}

// Rows that drag by ⠿ to put them in order: Mindset, Gratitude, and each category on Habits and Temptations. `texts` are
// the rows' words, in order; children(i, handle) draws row i with `handle`, its ⠿. onMove(from, to) saves the new order.
export function SortableRows({ texts, onMove, children }) {
  const sensors = useDragSensors();
  const ids = rowIds(texts);
  // A screen reader hears the row's words and its place, never the id.
  const place = (id) => `${texts[ids.indexOf(id)]}, place ${ids.indexOf(id) + 1} of ${ids.length}`;
  const announcements = {
    onDragStart: ({ active }) => `Picked up ${place(active.id)}.`,
    onDragOver: ({ over }) => (over ? `Moving to place ${ids.indexOf(over.id) + 1} of ${ids.length}.` : undefined),
    onDragEnd: ({ active, over }) => (over ? `Dropped ${texts[ids.indexOf(active.id)]} in place ${ids.indexOf(over.id) + 1} of ${ids.length}.` : `Dropped ${texts[ids.indexOf(active.id)]}.`),
    onDragCancel: ({ active }) => `Put ${texts[ids.indexOf(active.id)]} back.`,
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id), to = ids.indexOf(over.id);
    if (from >= 0 && to >= 0) onMove(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={{ announcements }}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {ids.map((id, i) => (
          <Sortable key={id} id={id}>{(drag) => children(i, <DragHandle label={texts[i]} {...drag} />)}</Sortable>
        ))}
      </SortableContext>
    </DndContext>
  );
}
